import { useState, useEffect, useCallback } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  connectionType?: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NetworkConnection extends EventTarget {
  readonly connectionType: string;
  readonly effectiveType: '2g' | '3g' | '4g' | 'slow-2g';
  readonly downlink: number;
  readonly rtt: number;
  readonly saveData: boolean;
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}

declare global {
  interface Navigator {
    connection?: NetworkConnection;
    mozConnection?: NetworkConnection;
    webkitConnection?: NetworkConnection;
  }
}

/**
 * Hook for monitoring network status and connection quality
 */
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    const connection = getNetworkConnection();
    return {
      isOnline: navigator.onLine,
      connectionType: connection?.connectionType as NetworkStatus['connectionType'],
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      saveData: connection?.saveData
    };
  });

  const updateNetworkStatus = useCallback(() => {
    const connection = getNetworkConnection();
    setNetworkStatus({
      isOnline: navigator.onLine,
      connectionType: connection?.connectionType as NetworkStatus['connectionType'],
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      saveData: connection?.saveData
    });
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: false }));
    };

    const handleConnectionChange = () => {
      updateNetworkStatus();
    };

    // Listen to online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen to connection changes (if supported)
    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateNetworkStatus, networkStatus]);

  return {
    ...networkStatus,
    isSlowConnection: networkStatus.effectiveType === 'slow-2g' || networkStatus.effectiveType === '2g',
    isFastConnection: networkStatus.effectiveType === '4g',
    shouldOptimizeForDataSaving: networkStatus.saveData || networkStatus.effectiveType === 'slow-2g'
  };
};

/**
 * Get network connection object from various browser implementations
 */
function getNetworkConnection(): NetworkConnection | undefined {
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
}

/**
 * Hook for handling offline state with queue management
 */
export const useOfflineManager = () => {
  const networkStatus = useNetworkStatus();
  const [offlineQueue, setOfflineQueue] = useState<Array<{
    id: string;
    action: () => Promise<any>;
    timestamp: number;
    retryCount: number;
  }>>([]);

  const addToQueue = useCallback((id: string, action: () => Promise<any>) => {
    if (networkStatus.isOnline) {
      // If online, execute immediately
      return action();
    }

    // Add to offline queue
    setOfflineQueue(prev => [
      ...prev.filter(item => item.id !== id), // Remove if already exists
      {
        id,
        action,
        timestamp: Date.now(),
        retryCount: 0
      }
    ]);

    return Promise.reject(new Error('Action queued for when connection is restored'));
  }, [networkStatus.isOnline]);

  const processQueue = useCallback(async () => {
    if (!networkStatus.isOnline || offlineQueue.length === 0) {
      return;
    }

    interface QueueProcessResult {
      id: string;
      success: boolean;
      result?: any;
      error?: any;
      retry?: boolean;
    }

    const results: QueueProcessResult[] = [];
    for (const item of offlineQueue) {
      try {
        const result = await item.action();
        results.push({ id: item.id, success: true, result });
      } catch (error) {
        console.error(`Failed to process queued action: ${item.id}`, error);
        
        // Retry logic
        if (item.retryCount < 3) {
          setOfflineQueue(prev => prev.map(queueItem => 
            queueItem.id === item.id 
              ? { ...queueItem, retryCount: queueItem.retryCount + 1 }
              : queueItem
          ));
          results.push({ id: item.id, success: false, error, retry: true });
        } else {
          results.push({ id: item.id, success: false, error, retry: false });
        }
      }
    }

    // Remove successfully processed items and items that exceeded retry limit
    setOfflineQueue(prev => prev.filter(item => {
      const result = results.find(r => r.id === item.id);
      return result && !result.success && result.retry;
    }));

    return results;
  }, [networkStatus.isOnline, offlineQueue]);

  const clearQueue = useCallback(() => {
    setOfflineQueue([]);
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    setOfflineQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  // Auto-process queue when coming back online
  useEffect(() => {
    if (networkStatus.isOnline && offlineQueue.length > 0) {
      const timer = setTimeout(processQueue, 1000); // Small delay to ensure stable connection
      return () => clearTimeout(timer);
    }
  }, [networkStatus.isOnline, offlineQueue.length, processQueue]);

  return {
    ...networkStatus,
    offlineQueue,
    queueSize: offlineQueue.length,
    addToQueue,
    processQueue,
    clearQueue,
    removeFromQueue
  };
};

/**
 * Hook for adaptive loading based on connection speed
 */
export const useAdaptiveLoading = () => {
  const networkStatus = useNetworkStatus();

  const getOptimalImageQuality = useCallback(() => {
    if (!networkStatus.isOnline) return 'low';
    if (networkStatus.saveData || networkStatus.effectiveType === 'slow-2g') return 'low';
    if (networkStatus.effectiveType === '2g') return 'medium';
    return 'high';
  }, [networkStatus]);

  const getOptimalChunkSize = useCallback(() => {
    if (!networkStatus.isOnline) return 0;
    if (networkStatus.effectiveType === 'slow-2g') return 5;
    if (networkStatus.effectiveType === '2g') return 10;
    if (networkStatus.effectiveType === '3g') return 20;
    return 50; // 4g or better
  }, [networkStatus]);

  const shouldPreloadContent = useCallback(() => {
    return networkStatus.isOnline && 
           !networkStatus.saveData && 
           (networkStatus.effectiveType === '3g' || networkStatus.effectiveType === '4g');
  }, [networkStatus]);

  const shouldEnableAutoPlay = useCallback(() => {
    return networkStatus.isOnline && 
           !networkStatus.saveData && 
           networkStatus.effectiveType === '4g';
  }, [networkStatus]);

  return {
    ...networkStatus,
    getOptimalImageQuality,
    getOptimalChunkSize,
    shouldPreloadContent,
    shouldEnableAutoPlay,
    recommendedTimeout: networkStatus.isSlowConnection ? 30000 : 10000
  };
};

export default useNetworkStatus;
